import { setLoginCookies } from '../api/customer/login';
import { pickAll, filter, identity, isEmpty, omit, pick } from 'ramda';
import { validateCustomerPj } from '../api/customer/validate';
import {
  BILLING_ADDRESS,
  BUYER_AND_COMPANY,
  DOCUMENT_CHECK,
  FINISH
} from './cadastro-pj-component';
import { dispatchSubmitEvent } from './metrics';
import { createCustomerPj } from '../api/customer/create';
import config from '../generated/config';
import { getAddressByZipCode } from '../api/cep/get';
import { getCustomerQualifyerData } from './customer-qualifyer';
import { hasNamedError } from '../commons/component/error';
import { getEventPayload } from '../metrics/metrics.selector';
import { dispatchMetric } from '../metrics';

const INPUT_ERRORS = [422, 409];

const {
  errorMessages: { blank: blankErrorMessages, wrongFormat: wrongFormatErrorMessages }
} = config.signUpScreen;

const eventType = 'event_signup';

export const dispatchMetricOnBuyerFormSubmit = () => {
  const metricPayload = {
    event: eventType,
    eventCategory: 'tela-cadastro-etapa-1',
    eventAction: 'continuar'
  };
  const metricEventPayload = getEventPayload({ ...metricPayload });
  dispatchMetric(eventType, {
    payload: {
      eventLabel: 'sucesso',
      ...metricEventPayload
    }
  });
};

const goToNextForm = (currentStep, setState) => {
  switch (currentStep) {
    default:
    case DOCUMENT_CHECK:
      setState({ formState: BUYER_AND_COMPANY });
      break;
    case BUYER_AND_COMPANY:
      setState({ formState: BILLING_ADDRESS });
      break;
    case BILLING_ADDRESS:
      setState({ formState: FINISH });
  }
};

export const goToPreviousForm = (currentStep, setState) => {
  switch (currentStep) {
    default:
    case FINISH:
      setState({ formState: BILLING_ADDRESS });
      break;
    case BILLING_ADDRESS:
      setState({ formState: BUYER_AND_COMPANY });
      break;
    case BUYER_AND_COMPANY:
      setState({ formState: DOCUMENT_CHECK });
      break;
  }
};

export const goBackToFirstForm = setState =>
  setState({ formState: DOCUMENT_CHECK, formDataState: {}, customerQualifyerData: {} });

export const clearBuyerAlreadyRegistered = setState => setState({ buyerAlreadyRegistered: false });

export const validateSubmit = async (
  formData,
  formFields,
  captcha,
  formDataState,
  setState,
  currentStep
) => {
  try {
    const captchaResponse = config.enableCaptchaOnPJRegister ? await captcha.getResponse() : '';
    const updatedFormDataState = { ...formDataState, ...formData };

    setState({ formDataState: updatedFormDataState });
    await validateCustomerPj(updatedFormDataState, captchaResponse);
    goToNextForm(currentStep, setState);
  } catch (errors) {
    if (errors !== 'request timeout' && config.enableCaptchaOnPJRegister) {
      captcha.reset();
    }
    const { message, statusCode } = errors;
    const filteredErrors = filter(identity, pickAll(formFields, message));
    if (!isEmpty(filteredErrors) && INPUT_ERRORS.includes(statusCode)) throw filteredErrors;
    goToNextForm(currentStep, setState);
  }
};

const updateErrors = errors => {
  if ('responsibleName' in errors.message) {
    return {
      ...omit(['responsibleName'], errors.message),
      contactName: errors.message.responsibleName
    };
  }

  if ('buyerAlreadyRegistered' in errors.message) {
    return {
      ...omit(['buyerAlreadyRegistered'], errors.message),
      buyerEmail: errors.message.buyerAlreadyRegistered
    };
  }

  return { ...errors.message };
};

export const getCompanyFormStatus = cadastroState => {
  const firstStepFields = {
    ...pick(['cnpj', 'corporateName', 'fantasyName'], cadastroState.company),
    billingEmail: cadastroState.company.billing.billingEmail
  };

  const secondStepFields = {
    ...omit(['billingEmail', 'reference', 'additionalInfo'], cadastroState.company.billing)
  };
  const firstStepFieldsValues = Object.values(firstStepFields);

  const secondStepFieldsValues = Object.values(secondStepFields);

  const isFirstStepFieldsFilled = firstStepFieldsValues.every(value => value !== '');

  const isSecondStepFieldsFilled = secondStepFieldsValues.every(value => value !== '');

  if (isFirstStepFieldsFilled && !isSecondStepFieldsFilled) {
    return 'SECOND_STEP';
  }

  if (isFirstStepFieldsFilled && isSecondStepFieldsFilled) {
    return 'THIRD_STEP';
  }

  return 'FIRST_STEP';
};

export const createCustomer = async (cadastroState, updateState, captcha, push) => {
  const metricPayload = {
    event: eventType,
    eventCategory: 'tela-cadastro-etapa-2',
    eventAction: 'continuar'
  };
  const metricEventPayload = getEventPayload({ ...metricPayload });

  try {
    const captchaResponse = config.enableCaptchaOnPJRegister ? await captcha.getResponse() : '';
    const { customer } = await createCustomerPj(cadastroState, captchaResponse);
    dispatchSubmitEvent(cadastroState);

    dispatchMetric(eventType, {
      payload: {
        eventLabel: 'sucesso',
        ...metricEventPayload
      }
    });
    setLoginCookies({
      ...customer,
      nickname: cadastroState.company.corporateName,
      privacyPolicy: 1
    });
    push('/cadastro-concluido');
  } catch (errors) {
    const { statusCode } = errors;

    dispatchMetric(eventType, {
      payload: {
        eventLabel: 'erro:campo-invalido',
        ...metricEventPayload
      }
    });

    if (statusCode === 409) {
      document.getElementById('cnpj-input').scrollIntoView({ block: 'end', behavior: 'smooth' });
    }

    const updatedErrors = updateErrors(errors);
    updateState({ ...cadastroState, errors: { ...updatedErrors } });

    const errorsMessageKeys = Object.keys(updatedErrors);

    if (Boolean(errorsMessageKeys.length)) {
      errorsMessageKeys.forEach(error => {
        if (error.includes('buyer')) {
          push('/cadastro/comprador');
        }
      });
    }

    throw errors;
  }
};

const DOCUMENT_MAX_LENGTH = 18;
const HTTP_BAD_REQUEST = 400;

export const getCep = async (event, cadastroState, updateState) => {
  event.preventDefault();
  const zipCode = event.target.value;
  updateState({
    ...cadastroState,
    company: {
      ...cadastroState.company,
      billing: { ...cadastroState.company.billing, zipCode }
    },
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  if (zipCode.length === 9 && zipCode !== cadastroState.company.billing.zipCode) {
    const { addressInfo, openAddressToEdit, errors } = await getAddressByZipCode(zipCode);

    updateState({
      ...cadastroState,
      isFormSubmitting: true
    });

    dispatchSubmitEvent(cadastroState, errors);

    if (!addressInfo) {
      updateState({
        ...cadastroState,
        company: {
          ...cadastroState.company,
          billing: { ...cadastroState.company.billing, zipCode }
        },
        errors,
        isFormSubmitting: false
      });
      //   moveToFirstError(errors);
      return;
    }

    const { address, neighborhood, city, state, complement } = addressInfo;

    updateState({
      ...cadastroState,
      company: {
        ...cadastroState.company,
        billing: {
          ...cadastroState.company.billing,
          address,
          neighborhood,
          city,
          state,
          additionalInfo: complement,
          zipCode
        }
      },
      errors: {
        ...cadastroState.errors,
        ...errors
      },
      openAddressToEdit,
      isFormSubmitting: false
    });
  }
};

export const updateCnpj = async (event, cadastroState, updateState) => {
  event.preventDefault();
  const cnpj = event.target.value;

  updateState({
    ...cadastroState,
    company: { ...cadastroState.company, cnpj },
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });

  if (cnpj.length === DOCUMENT_MAX_LENGTH) {
    updateState({
      ...cadastroState,
      company: { ...cadastroState.company, cnpj },
      isFormSubmitting: true,
      customerQualifyerData: {}
    });
    const customerQualifyerData = await getCustomerQualifyerData(cnpj);
    updateState({
      ...cadastroState,
      company: { ...cadastroState.company, cnpj },
      customerQualifyerData
    });

    if (customerQualifyerData.success) {
      updateState({
        ...cadastroState,
        company: {
          ...cadastroState.company,
          cnpj,
          corporateName: customerQualifyerData.company.corporateName || '',
          fantasyName: customerQualifyerData.company.fantasyName || '',
          billing: {
            ...cadastroState.company.billing,
            address: customerQualifyerData.address.publicPlace || '',
            neighborhood: customerQualifyerData.address.neighborhood || '',
            city: customerQualifyerData.address.microRegion || '',
            state: customerQualifyerData.address.fu || '',
            number: customerQualifyerData.address.number || '',
            zipCode: customerQualifyerData.address.zipCode || ''
          }
        },
        errors: {},
        isFormSubmitting: false
      });
    } else {
      if (customerQualifyerData.response) {
        const { status } = customerQualifyerData.response;
        updateState({
          ...cadastroState,
          company: { ...cadastroState.company, cnpj },
          isFormSubmitting: false,
          errors: getCnpjStatusErrorMessage(status)
        });
      }
    }
  }
};

const getCnpjStatusErrorMessage = status => {
  if (!status) {
    return {};
  }
  if (status === HTTP_BAD_REQUEST) {
    return { cnpj: 'CNPJ inválido.' };
  }
  // else {
  //   return { cnpj: 'Este CNPJ não se encontra ativo/válido na Receita Federal.' };
  // }
};

const isBlankField = value => !Boolean(value);

export const setBlankErrorMessage = (inputName, cadastroState, updateState) => {
  updateState({
    ...cadastroState,
    errors: { ...cadastroState.errors, [inputName]: blankErrorMessages[inputName] }
  });
};

const setWrongFormatErrorMessage = (inputName, cadastroState, updateState) => {
  updateState({
    ...cadastroState,
    errors: { ...cadastroState.errors, [inputName]: wrongFormatErrorMessages[inputName] }
  });
  return;
};

export const removeErrorMessage = (inputName, cadastroState, updateState) => {
  const filteredErrors = omit([inputName], cadastroState.errors);
  updateState({
    ...cadastroState,
    errors: { ...filteredErrors }
  });
  return;
};

const namePattern = /^[a-zA-Z\u00C0-\u017F´]+\s+([a-zA-Z\u00C0-\u017F´]\s?){0,}$/;

const addressPattern = /^([a-zA-Z\u00C0-\u017F´]\s?){0,}$/;

// eslint-disable-next-line
const emailPattern = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

export const checkNameField = (value, inputName, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  if (value.match(namePattern) && value.trim().split(' ').length >= 2) {
    removeErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  setWrongFormatErrorMessage(inputName, cadastroState, updateState);
  return;
};

export const checkBuyerPhoneField = (value, inputName, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  if (value.length < 15 && value !== '') {
    setWrongFormatErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  removeErrorMessage(inputName, cadastroState, updateState);
  return;
};

export const checkBuyerEmailField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('buyerEmail', cadastroState, updateState);
    return;
  }
  if (value.match(emailPattern) && value !== '') {
    removeErrorMessage('buyerEmail', cadastroState, updateState);
    return;
  }
  setWrongFormatErrorMessage('buyerEmail', cadastroState, updateState);
  return;
};

export const checkPasswordField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('buyerPassword', cadastroState, updateState);
    return;
  }
  if (value.length < 6 && value !== '') {
    setWrongFormatErrorMessage('buyerPassword', cadastroState, updateState);
    return;
  }
  removeErrorMessage('buyerPassword', cadastroState, updateState);
  return;
};

export const checkCnpjField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('cnpj', cadastroState, updateState);
    return;
  }
  if (value.length < 18 && value !== '') {
    setWrongFormatErrorMessage('cnpj', cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage('cnpj', cadastroState, updateState);
  return;
};

export const checkCompanyName = (value, inputName, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  if ((value.length < 3 || value.length > 60) && value !== '') {
    setWrongFormatErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage(inputName, cadastroState, updateState);
  return;
};

export const checkEmailField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('email', cadastroState, updateState);
    return;
  }
  if (value.match(emailPattern) && value !== '') {
    updateState({
      ...cadastroState,
      companyFormStatus: getCompanyFormStatus(cadastroState)
    });
    removeErrorMessage('email', cadastroState, updateState);
    return;
  }
  setWrongFormatErrorMessage('email', cadastroState, updateState);
  return;
};

export const checkZipCodeField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('zipCode', cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage('zipCode', cadastroState, updateState);
  return;
};

export const checkRequiredAddressField = (value, inputName, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  if (value >= 3 && value <= 47) {
    setWrongFormatErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage(inputName, cadastroState, updateState);
  return;
};

export const checkUnrequiredAddressField = (value, inputName, cadastroState, updateState) => {
  if (value >= 3 && value <= 47) {
    setWrongFormatErrorMessage(inputName, cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage(inputName, cadastroState, updateState);
  return;
};

export const checkStateField = (value, cadastroState, updateState) => {
  if (value.length !== 2 && value !== '') {
    setWrongFormatErrorMessage('state', cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage('state', cadastroState, updateState);
  return;
};

export const checkAddressField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('address', cadastroState, updateState);
    return;
  }
  if (value.match(addressPattern) && value !== '' && value.length >= 3 && value.length <= 60) {
    updateState({
      ...cadastroState,
      companyFormStatus: getCompanyFormStatus(cadastroState)
    });
    removeErrorMessage('address', cadastroState, updateState);
    return;
  }
  setWrongFormatErrorMessage('address', cadastroState, updateState);
  return;
};

export const checkNumberField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('number', cadastroState, updateState);
    return;
  }
  if (value.length > 5 && value !== '') {
    setWrongFormatErrorMessage('number', cadastroState, updateState);
    return;
  }
  updateState({
    ...cadastroState,
    companyFormStatus: getCompanyFormStatus(cadastroState)
  });
  removeErrorMessage('number', cadastroState, updateState);
  return;
};

export const checkPhoneField = (value, cadastroState, updateState) => {
  if (isBlankField(value)) {
    setBlankErrorMessage('phone', cadastroState, updateState);
    return;
  }
  if ((value.length < 14 || value.length > 15) && value !== '') {
    setWrongFormatErrorMessage('phone', cadastroState, updateState);
    return;
  }
  removeErrorMessage('phone', cadastroState, updateState);
  return;
};

export const checkPhoneExtensionRegistryField = (value, cadastroState, updateState) => {
  if (value.length > 5 && value !== '') {
    setWrongFormatErrorMessage('phoneExtensionRegistry', cadastroState, updateState);
    return;
  }
};

export const hasPatternError = errors => {
  const errorsObject = Boolean(errors) ? { ...errors } : {};
  return Object.keys(errorsObject).length > 0;
};

export const hasError = (name, errors) => {
  return hasNamedError(name, errors) ? '--hasError' : '';
};
